import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Clock, Loader2, CheckCircle2 } from "lucide-react";

const DEFAULT_SHIFTS = [
  { id: "matutino", name: "Matutino", student_in: "", student_out: "", teacher_in: "", teacher_out: "" },
  { id: "vespertino", name: "Vespertino", student_in: "", student_out: "", teacher_in: "", teacher_out: "" },
  { id: "completo", name: "Tiempo completo", student_in: "", student_out: "", teacher_in: "", teacher_out: "" },
];

const slug = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 32);

export function ShiftsSettings({ user, checkAuth }) {
  const [shifts, setShifts] = useState(() => (user?.shifts?.length ? user.shifts.map((s) => ({ ...s })) : DEFAULT_SHIFTS.map((s) => ({ ...s }))));
  const [saving, setSaving] = useState(false);

  const update = (i, field, val) => setShifts((prev) => prev.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)));
  const remove = (i) => setShifts((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => {
    const base = "turno";
    let id = base, n = 1;
    const ids = new Set(shifts.map((s) => s.id));
    while (ids.has(id)) id = `${base}-${++n}`;
    setShifts((prev) => [...prev, { id, name: "Nuevo turno", student_in: "", student_out: "", teacher_in: "", teacher_out: "" }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = shifts
        .filter((s) => (s.name || "").trim())
        .map((s) => ({ ...s, id: slug(s.id) || slug(s.name), name: s.name.trim() }));
      const ids = new Set();
      const dedup = clean.filter((s) => (s.id && !ids.has(s.id) ? (ids.add(s.id), true) : false));
      await api.put("/auth/profile", { shifts: dedup });
      await checkAuth?.();
      setShifts(dedup);
      toast.success("Turnos guardados. Aplican al registrar entrada/salida según el turno de cada persona.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron guardar los turnos");
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-10 pt-6 border-t border-nardo-200" data-testid="shifts-section">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <h3 className="font-display font-semibold text-lg text-navy flex items-center gap-2"><Clock className="w-4 h-4" /> Turnos (horarios por jornada)</h3>
          <p className="text-sm text-nardo-500">Configura horarios distintos por turno. Si una persona tiene turno asignado, su retardo/salida anticipada se calcula con estas horas; si no, usa las horas predeterminadas de arriba.</p>
        </div>
        <button type="button" onClick={add} data-testid="shift-add-btn"
          className="shrink-0 inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> Turno
        </button>
      </div>

      <div className="space-y-4 mt-4">
        {shifts.map((s, i) => (
          <div key={i} className="border border-nardo-200 rounded-lg p-4" data-testid={`shift-row-${i}`}>
            <div className="flex items-center justify-between gap-2 mb-3">
              <input value={s.name} onChange={(e) => update(i, "name", e.target.value)} maxLength={40}
                data-testid={`shift-name-${i}`} placeholder="Nombre del turno"
                className="font-semibold text-navy bg-transparent border-b border-nardo-200 focus:border-navy outline-none py-1 w-full max-w-xs" />
              <button type="button" onClick={() => remove(i)} data-testid={`shift-remove-${i}`}
                className="text-nardo-400 hover:text-red-600 p-1 shrink-0" title="Eliminar turno"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-nardo-500">Alumnos · entrada límite</label>
                <input type="time" value={s.student_in || ""} onChange={(e) => update(i, "student_in", e.target.value)} data-testid={`shift-${i}-student-in`}
                  className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-1.5 focus:border-navy outline-none text-navy text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-nardo-500">Alumnos · salida mínima</label>
                <input type="time" value={s.student_out || ""} onChange={(e) => update(i, "student_out", e.target.value)} data-testid={`shift-${i}-student-out`}
                  className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-1.5 focus:border-navy outline-none text-navy text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-nardo-500">Docentes/Personal · entrada límite</label>
                <input type="time" value={s.teacher_in || ""} onChange={(e) => update(i, "teacher_in", e.target.value)} data-testid={`shift-${i}-teacher-in`}
                  className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-1.5 focus:border-navy outline-none text-navy text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-nardo-500">Docentes/Personal · salida mínima</label>
                <input type="time" value={s.teacher_out || ""} onChange={(e) => update(i, "teacher_out", e.target.value)} data-testid={`shift-${i}-teacher-out`}
                  className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-1.5 focus:border-navy outline-none text-navy text-sm" />
              </div>
            </div>
          </div>
        ))}
        {shifts.length === 0 && <p className="text-sm text-nardo-400">No hay turnos. Agrega uno con el botón "Turno".</p>}
      </div>

      <button type="button" onClick={save} disabled={saving} data-testid="shifts-save-btn"
        className="mt-5 inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-6 py-2.5 rounded-md transition-colors text-sm font-medium disabled:opacity-50">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Guardar turnos
      </button>
    </div>
  );
}
